import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Question } from '../../schemas/schemas';

interface QuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (question: Question) => void;
  editingQuestion: Question | null;
  translations?: {
    title?: string;
    editQuestion?: string;
    addQuestion?: string;
    questionText?: string;
    questionType?: string;
    answerOptions?: string;
    correctAnswer?: string;
    difficulty?: string;
    tags?: string;
    cancel?: string;
    save?: string;
    saveChanges?: string;
    addOption?: string;
    [key: string]: string | undefined;
  };
}

export default function QuestionModal({ isOpen, onClose, onSave, editingQuestion, translations = {} }: QuestionModalProps) {
  // Get the initial question type from the editing question or default to multiple choice
  const initialType = editingQuestion?.question_type || 'multiple_choice';
  const [questionType, setQuestionType] = useState<string>(initialType);
  
  // Get options for multiple choice questions 
  const initialOptions = (): string[] => {
    if (editingQuestion && editingQuestion.question_type === 'multiple_choice') {
      return editingQuestion.options || ['', ''];
    }
    return ['', ''];
  };
  
  const [options, setOptions] = useState<string[]>(initialOptions());
  
  // Early return if modal is not open
  if (!isOpen) return null;
  
  const isEditing = !!editingQuestion;
  
  // Handle form submission
  const handleSave = () => {
    // Create a new question or modify the existing one
    let newQuestion: Question;
    
    // Generate a random ID if not editing
    const questionId = editingQuestion?.question_id || `q${Date.now()}`;
    const text = document.getElementById('question-text') as HTMLTextAreaElement;
    
    // This is simplified - in a real app, we would use proper form handling
    if (questionType === 'multiple_choice') {
      // We know this will be a multiple choice question
      newQuestion = {
        question_id: questionId,
        question_text: text.value || 'Sample multiple choice question',
        question_type: 'multiple_choice',
        options: options.length ? options : ['Option A', 'Option B', 'Option C'],
        correct_option: options[0] || 'Option A', // Simplified - should come from selected radio
      };
    } else if (questionType === 'boolean') {
      // Get the selected boolean value
      const trueRadio = document.getElementById('true-answer') as HTMLInputElement;
      
      newQuestion = {
        question_id: questionId,
        question_text: text.value || 'Sample true/false question',
        question_type: 'boolean',
        correct_answer: trueRadio.checked ? 'true' : 'false',
      };
    } else {
      // Must be a text question
      const textAnswer = document.getElementById('text-answer') as HTMLInputElement;
      
      newQuestion = {
        question_id: questionId,
        question_text: text.value || 'Sample text input question',
        question_type: 'text',
        correct_answer: textAnswer.value || '',
      };
    }
    
    onSave(newQuestion);
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>{isEditing ? (translations.editQuestion || 'Edit Question') : (translations.addQuestion || 'Add New Question')}</CardTitle>
          <CardDescription>
            {isEditing ? 'Modify the selected question' : 'Create a new question for this exercise'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label htmlFor="question-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {translations.questionText || 'Question Text'}
              </label>
              <textarea
                id="question-text"
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                placeholder="Enter the question text"
                defaultValue={editingQuestion?.question_text || ''}
              ></textarea>
            </div>
            
            <div>
              <label htmlFor="question-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {translations.questionType || 'Question Type'}
              </label>
              <select
                id="question-type"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                defaultValue={initialType}
                onChange={(e) => setQuestionType(e.target.value)}
              >
                <option value="multiple_choice">Multiple Choice</option>
                <option value="boolean">True/False</option>
                <option value="text">Text Input</option>
                <option value="fill_in_blank">Fill in the Blank</option>
              </select>
            </div>
            
            {questionType === 'multiple_choice' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {translations.answerOptions || 'Answer Options'}
                </label>
                <div className="mt-2 space-y-2">
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center">
                      <input
                        type="text"
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                        placeholder={`Option ${index + 1}`}
                        defaultValue={option}
                        onChange={(e) => {
                          const newOptions = [...options];
                          newOptions[index] = e.target.value;
                          setOptions(newOptions);
                        }}
                      />
                      <div className="ml-2">
                        <input
                          type="radio"
                          name="correct-answer"
                          className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                          defaultChecked={editingQuestion?.question_type === 'multiple_choice' && editingQuestion.correct_option === option}
                        />
                        <label className="ml-1 text-xs text-gray-500">Correct</label>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="ml-2 text-red-500"
                        onClick={() => {
                          if (options.length > 2) {
                            setOptions(options.filter((_, i) => i !== index));
                          }
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </Button>
                    </div>
                  ))}
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setOptions([...options, ''])}
                  >
                    {translations.addOption || 'Add Option'}
                  </Button>
                </div>
              </div>
            )}
            
            {questionType === 'boolean' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {translations.correctAnswer || 'Correct Answer'}
                </label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center">
                    <input
                      id="true-answer"
                      name="boolean-answer"
                      type="radio"
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      defaultChecked={editingQuestion?.correct_answer === 'true'}
                    />
                    <label htmlFor="true-answer" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      True
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="false-answer"
                      name="boolean-answer"
                      type="radio"
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      defaultChecked={editingQuestion?.correct_answer === 'false'}
                    />
                    <label htmlFor="false-answer" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      False
                    </label>
                  </div>
                </div>
              </div>
            )}
            
            {(questionType === 'text' || questionType === 'fill_in_blank') && (
              <div>
                <label htmlFor="text-answer" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {translations.correctAnswer || 'Correct Answer'}
                </label>
                <input
                  id="text-answer"
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                  placeholder="Enter the correct answer"
                  defaultValue={editingQuestion?.correct_answer || ''}
                />
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>
            {translations.cancel || 'Cancel'}
          </Button>
          <Button onClick={handleSave}>
            {isEditing ? (translations.saveChanges || 'Save Changes') : (translations.save || 'Save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
} 